/**
 * KAN-925: вошедшие аккаунты браузера и действия переключателя.
 * - accounts / active: список и активный аккаунт этой вкладки (меняются
 *   действиями в этой вкладке; список подтягивается и из соседних вкладок);
 * - switchTo: другой аккаунт становится активным во вкладке, кеш react-query
 *   и кеш файлов сбрасываются, полная перезагрузка на адрес как после обычного
 *   входа (pathAfterLogin): данные прошлого аккаунта не мелькают;
 * - add: страница входа в режиме добавления, вошедшие не выходят;
 * - logout: выходит только текущий (refresh отзывается), перезагрузка на
 *   следующий или переход на вход;
 * - logoutAll: выходят все, переход на вход.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getAccountsSnapshot,
  setActiveAccount,
  subscribeAccounts,
  watchTabAccount,
  type Account,
  type AccountsSnapshot,
} from '@/lib/accounts';
import { clearApiFileCache } from '@/lib/apiFileCache';
import { endAllAuthSessions, endAuthSession } from '@/lib/authSession';
import { ADD_ACCOUNT_PATH, loginPathAfterLogout, pathAfterLogin } from '@/lib/afterLogin';
import { isManagerUser } from '@/lib/managerAccess';

export interface AccountsState {
  accounts: Account[];
  active: Account | null;
  switchTo: (userId: string) => void;
  add: () => void;
  logout: () => void;
  logoutAll: () => void;
}

function homeOf(account: Account): string {
  return pathAfterLogin(isManagerUser({ role: account.role ?? undefined }));
}

/**
 * KAN-925: аккаунт вкладки удалён в другой вкладке: кеш сбрасывается и вкладка
 * перезагружается (AuthGuard уведёт на вход), а не продолжает с чужим токеном.
 */
export function useTabAccountWatch(): void {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      watchTabAccount(() => {
        queryClient.clear();
        window.location.reload();
      }),
    [queryClient]
  );
}

/** Только список и активный аккаунт (без действий), для страниц, которым нужно "кто вошёл". */
export function useAccountsSnapshot(): AccountsSnapshot {
  return useSyncExternalStore(subscribeAccounts, getAccountsSnapshot, getAccountsSnapshot);
}

export function useAccounts(): AccountsState {
  const { accounts, active } = useAccountsSnapshot();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const switchTo = useCallback(
    (userId: string) => {
      const target = getAccountsSnapshot().accounts.find((a) => a.userId === userId);
      if (!target || getAccountsSnapshot().active?.userId === userId) return;
      setActiveAccount(userId);
      queryClient.clear();
      clearApiFileCache();
      // полная перезагрузка: данные прошлого аккаунта не мелькают, модульные хранилища сброшены
      window.location.assign(homeOf(target));
    },
    [queryClient]
  );

  const add = useCallback(() => navigate(ADD_ACCOUNT_PATH), [navigate]);

  const logout = useCallback(() => {
    const next = endAuthSession();
    queryClient.clear();
    // следующий аккаунт открывается полной перезагрузкой (как переключение)
    if (next) window.location.assign(homeOf(next));
    else navigate(loginPathAfterLogout(pathname));
  }, [navigate, pathname, queryClient]);

  const logoutAll = useCallback(() => {
    endAllAuthSessions();
    queryClient.clear();
    navigate(loginPathAfterLogout(pathname));
  }, [navigate, pathname, queryClient]);

  return { accounts, active, switchTo, add, logout, logoutAll };
}
